import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Calendar, FileText, Award, ChevronRight, Loader2 } from "lucide-react";
import { useAuthSession } from "@/hooks/useAuthSession";
import { supabase } from "@/lib/supabase";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";

interface QuizResult {
  id: string;
  user_id: string;
  subject: string;
  category: string;
  score: number;
  total_questions: number;
  percentage: number;
  completed_at: string;
}

const QuizHistory = () => {
  const [quizResults, setQuizResults] = useState<QuizResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { userId } = useAuthSession();
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();

  useEffect(() => {
    const fetchQuizHistory = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('quiz_results')
          .select('*')
          .eq('user_id', userId)
          .order('completed_at', { ascending: false });
        
        if (error) throw error;
        
        console.log('Quiz history data:', data);
        setQuizResults(data || []);
      } catch (err) {
        console.error('Error fetching quiz history:', err);
        setError('Failed to load quiz history. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchQuizHistory();
  }, [userId]);

  const handleViewResult = (sessionId: string) => {
    navigate(`/quiz/results/${sessionId}`);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), isMobile ? 'dd-MMM' : 'dd-MMM-yyyy');
    } catch (err) {
      return 'Invalid date';
    }
  };

  // Add a console.log to verify component rendering
  console.log('Rendering Quiz History component, found results:', quizResults?.length);

  return (
    <div className={`container mx-auto py-3 ${isMobile ? 'px-0' : 'px-6'} max-w-4xl`}>
      <Card className="shadow-lg hover:shadow-xl transition-all duration-300 animate-fadeIn border-0 overflow-hidden">
        <CardHeader className={`bg-gradient-to-r from-indigo-600 to-purple-600 text-white ${isMobile ? 'p-4' : 'p-6'} rounded-t-lg`}>
          <CardTitle className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold flex items-center`}>
            <FileText className="mr-3 h-5 w-5" />
            My Quiz History
          </CardTitle>
        </CardHeader>
        <CardContent className={`${isMobile ? 'p-3' : 'p-6'} bg-gradient-to-b from-white to-blue-50`}>
          {loading ? (
            <div className="flex justify-center items-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="ml-2 text-lg text-gray-600">Loading quiz history...</span>
            </div>
          ) : error ?  (
            <div className="text-center py-10">
              <p className="text-red-500">{error}</p>
            </div>
          ) : quizResults.length === 0 ? (
            <div className="text-center py-10 space-y-4">
              <div className="bg-blue-50 p-8 rounded-xl inline-flex flex-col items-center mx-auto">
                <FileText className="h-16 w-16 text-indigo-400 mb-4" />
                <p className="text-gray-600 text-xl mb-2">No quiz history found.</p>
                <p className="text-gray-400">Complete a quiz to see your results here!</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-3 sm:mx-0">
              {isMobile ? (
                <div className="space-y-3 px-3">
                  {quizResults.map((result) => (
                    <div
                      key={result.id}
                      className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 hover:border-indigo-200 transition-all"
                      onClick={() => handleViewResult(result.id)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-medium text-primary">{result.subject}</div>
                          <div className="text-xs text-gray-500">{result.category}</div>
                        </div>
                        <div className="text-xs text-gray-500">{formatDate(result.completed_at)}</div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium 
                          ${result.percentage >= 80 
                            ? 'bg-gradient-to-r from-green-400 to-green-500 text-white' 
                            : result.percentage >= 60 
                            ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white'
                            : 'bg-gradient-to-r from-red-400 to-red-500 text-white'}`}>
                          {result.score}/{result.total_questions} ({result.percentage}%)
                        </span>
                        
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-7 text-xs"
                        >
                          View <ChevronRight className="ml-1 h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-indigo-50">
                      <TableHead className="w-1/4"><span className="flex items-center"><Calendar className="mr-2 h-4 w-4 text-indigo-500" /> Date</span></TableHead>
                      <TableHead className="w-1/4">Subject/Topic</TableHead>
                      <TableHead className="w-1/4"><span className="flex items-center"><Award className="mr-2 h-4 w-4 text-indigo-500" /> Score</span></TableHead>
                      <TableHead className="w-1/4 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quizResults.map((result) => (
                      <TableRow 
                        key={result.id} 
                        className="hover:bg-blue-50 cursor-pointer transition-colors duration-200 group animate-fadeIn"
                        onClick={() => handleViewResult(result.id)}
                      >
                        <TableCell className="font-medium">{formatDate(result.completed_at)}</TableCell>
                        <TableCell>
                          <div className="font-medium text-primary">{result.subject}</div>
                          <div className="text-sm text-gray-500">{result.category}</div>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center px-2.5 py-1.5 rounded-full text-xs font-medium 
                            ${result.percentage >= 80 
                              ? 'bg-gradient-to-r from-green-400 to-green-500 text-white' 
                              : result.percentage >= 60 
                              ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white'
                              : 'bg-gradient-to-r from-red-400 to-red-500 text-white'}`}>
                            {result.score}/{result.total_questions} ({result.percentage}%)
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewResult(result.id);
                            }}
                            className="hover:bg-indigo-100 hover:text-indigo-700 group-hover:translate-x-1 transition-transform"
                          >
                            View Results <ChevronRight className="ml-1 h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QuizHistory;
